/** @file /teams list view with CRUD + apply-to-current-play. */
import { h, clear } from "./dom.js";
import { authStore } from "../core/auth-store.js";
import {
  listTeams,
  createTeam,
  updateTeam,
  deleteTeam,
} from "../persistence/teams-repo.js";
import { openTeamEditor } from "./team-editor-view.js";
import { openConfirmDialog } from "./confirm-dialog.js";
import { showToast } from "./toast.js";
import { applyTeamToTokens } from "../core/team-apply.js";
import { appStore } from "../core/state.js";
import { navigate } from "../core/router.js";

/** @typedef {import("../types/schemas.js").TeamDoc} TeamDoc */

/**
 * @param {HTMLElement} host
 */
export function mountTeamsView(host) {
  clear(host);
  const container = h("div", { class: "max-w-3xl mx-auto p-6" });
  host.appendChild(container);

  const header = h("div", { class: "flex items-center justify-between mb-4" });
  const title = h("h1", { class: "text-2xl font-bold" }, ["Meine Teams"]);
  const newBtn = h("button", { type: "button", class: "hb-btn hb-btn--primary min-h-11" }, [
    "+ Neues Team",
  ]);
  header.appendChild(title);
  header.appendChild(newBtn);
  container.appendChild(header);

  const list = h("div", { class: "flex flex-col gap-2" });
  container.appendChild(list);

  async function refresh() {
    const uid = authStore.get().user?.uid;
    if (!uid) {
      return;
    }
    try {
      const teams = await listTeams(uid);
      render(teams);
    } catch (err) {
      console.warn("[teams] list failed", err);
      showToast("Teams konnten nicht geladen werden");
    }
  }

  /** @param {TeamDoc[]} teams */
  function render(teams) {
    clear(list);
    if (teams.length === 0) {
      list.appendChild(
        h("div", { class: "text-neutral-400 text-center py-8" }, [
          "Noch keine Teams. Lege eins an, um Kader beim Speichern vorzubelegen.",
        ]),
      );
      return;
    }
    for (const team of teams) {
      list.appendChild(makeRow(team));
    }
  }

  /** @param {TeamDoc} team @returns {HTMLElement} */
  function makeRow(team) {
    const swatch = h("span", { class: "hb-swatch" });
    swatch.style.background = team.color;
    const roster = h("span", { class: "text-sm text-neutral-400" }, [
      `Kader (${team.roster.length} Spieler)`,
    ]);
    const nameEl = h("span", { class: "font-semibold" }, [team.name]);
    const editBtn = h("button", { type: "button", class: "hb-btn min-h-11" }, ["Bearbeiten"]);
    const applyBtn = h("button", { type: "button", class: "hb-btn min-h-11" }, [
      "Auf Spielzug anwenden",
    ]);
    const delBtn = h("button", { type: "button", class: "hb-btn hb-btn--danger min-h-11" }, [
      "Löschen",
    ]);

    editBtn.addEventListener("click", async () => {
      const uid = authStore.get().user?.uid;
      if (!uid) {
        return;
      }
      const patch = await openTeamEditor({ team });
      if (!patch) {
        return;
      }
      try {
        await updateTeam(uid, team.id, /** @type {any} */ (patch));
        showToast("Team aktualisiert");
        refresh();
      } catch (err) {
        console.warn("[teams] update failed", err);
        showToast("Aktualisieren fehlgeschlagen");
      }
    });

    applyBtn.addEventListener("click", () => {
      const state = appStore.get();
      const tokens = applyTeamToTokens(state.tokens, team);
      appStore.set({ tokens });
      showToast("Team auf Spielzug angewendet");
      navigate("editor");
    });

    delBtn.addEventListener("click", async () => {
      const uid = authStore.get().user?.uid;
      if (!uid) {
        return;
      }
      const ok = await openConfirmDialog({
        title: "Team löschen?",
        body: `'${team.name}' inklusive aller Spielerdaten löschen?`,
        destructive: true,
        confirmLabel: "Löschen",
      });
      if (!ok) {
        return;
      }
      try {
        await deleteTeam(uid, team.id);
        showToast("Team gelöscht");
        refresh();
      } catch (err) {
        console.warn("[teams] delete failed", err);
        showToast("Löschen fehlgeschlagen");
      }
    });

    return h(
      "div",
      { class: "flex items-center gap-3 p-3 bg-neutral-800 rounded-lg flex-wrap" },
      [swatch, nameEl, roster, h("div", { class: "ml-auto flex gap-2 flex-wrap" }, [applyBtn, editBtn, delBtn])],
    );
  }

  newBtn.addEventListener("click", async () => {
    const uid = authStore.get().user?.uid;
    if (!uid) {
      return;
    }
    const draft = await openTeamEditor({ team: null });
    if (!draft) {
      return;
    }
    try {
      await createTeam(uid, /** @type {any} */ (draft));
      showToast("Team angelegt");
      refresh();
    } catch (err) {
      console.warn("[teams] create failed", err);
      showToast("Anlegen fehlgeschlagen");
    }
  });

  refresh();
}
